import { useEffect, useMemo, useState } from "react";

import BankTransaction from "../services/BankTransaction";
import PaymentSystem, {
	PaymentProvider,
	PaymentSystemFrontEndType,
} from "../services/PaymentSystem";
import composeTripReportFromTransactions from "../utils/composeTripReport.util";

import useModelSubscribe from "./useModelSubscribe2";

/**
 * Загружает банковские транзакции заказа и собирает отчёт о поездке
 * из всей цепочки (холд + досписание/возврат), а не из последней BT.
 * Чекбокс «создание отчётов о поездке» управляет только видимостью.
 */
export default function useOrderTripReport(orderId?: number | null) {
	const [loading, setLoading] = useState(false);
	const [items, setItems] = useState<BankTransaction.Model[]>([]);

	const paymentSystems = useModelSubscribe(
		{ types: [PaymentSystemFrontEndType.CUSTOMER] },
		PaymentSystem,
	);

	useEffect(() => {
		let cancelled = false;

		if (!orderId) {
			setItems([]);
			return undefined;
		}

		setLoading(true);
		BankTransaction.getAll({ orderIds: [orderId], limit: 50 })
			.then((response) => {
				if (!cancelled) setItems(response?.items ?? []);
			})
			.catch(() => {
				if (!cancelled) setItems([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [orderId]);

	const transaction = useMemo(() => {
		const candidates = items.filter(
			(item) =>
				item.status ===
					BankTransaction.PaymentTransactionStatus.COMPLETED &&
				item.tripReport,
		);

		if (!candidates.length) return null;

		return candidates.sort(
			(left, right) =>
				new Date(right.createdAt ?? 0).getTime() -
				new Date(left.createdAt ?? 0).getTime(),
		)[0];
	}, [items]);

	// Видимость по настройке платёжной системы Victoriabank (Cybersource).
	// Если систему после оплаты удалили — отчёт продолжаем показывать.
	const visible = useMemo(() => {
		if (!transaction) return false;

		const matching = (paymentSystems.models ?? []).filter(
			(system) =>
				system.paymentProvider === PaymentProvider.CYBERSOURCE &&
				(!transaction.taxiServiceId ||
					!system.taxiServiceIds?.length ||
					system.taxiServiceIds.includes(transaction.taxiServiceId)),
		);

		if (!matching.length) return true;

		return matching.some(
			(system) => system.cybersourceCreateTripReports !== false,
		);
	}, [transaction, paymentSystems.models]);

	const tripReport = useMemo(() => {
		if (!visible) return null;
		return composeTripReportFromTransactions(items);
	}, [visible, items]);

	return { tripReport, transaction, loading };
}
